import React from 'react';
import { useApolloClient } from '@apollo/client';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';

import Accordion, {
  AccordionContent,
  AccordionTitle
} from '@buyside/components/presentation/Accordion/accordionComponents';
import DelayedAccessAlert, {
  Location
} from '@buyside/components/presentation/AccessControl/delayedAccessAlert';
import IdeaUpdateCard from './IdeaUpdateCard/ideaUpdateCard';
import IdeaUpdateModal from '@buyside/components/presentation/IdeaShowPage/AuthorActions/ideaUpdateModal';
import IdeaUpdateCardLoading from '@buyside/components/presentation/IdeaUpdates/IdeaUpdateCard/ideaUpdateCardLoading';

import { times } from '@buyside/util/lodashAlt';

import {
  IdeaAuthorQuery,
  IdeaCoreQuery,
  IdeaCurrentUserQuery,
  IdeaPositionsQuery,
  Maybe,
  useIdeaUpdatesQuery
} from '@interfaces/graphql-typings';

const IdeaAuthor = require('buyside/graphql/queries/ideaShowPage/IdeaAuthor');
const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');
const IdeaCurrentUser = require('buyside/graphql/queries/ideaShowPage/IdeaCurrentUser');
const IdeaPositions = require('buyside/graphql/queries/ideaShowPage/IdeaPositions');

const IdeaUpdates: React.FC<{
  authorDisplayName?: Maybe<string>;
  canWriteComments: boolean;
  ideaId: string;
  isAuthor: boolean;
  isPublic: boolean;
  loading: boolean;
}> = ({ authorDisplayName, canWriteComments, ideaId, isAuthor, isPublic, loading: isLoading }) => {
  const client = useApolloClient();

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUser,
    variables: { ideaId }
  })?.currentUser;

  const cachedIdeaAuthor = client.readQuery<IdeaAuthorQuery>({
    query: IdeaAuthor,
    variables: { ideaId }
  })?.idea;

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId }
  })?.idea;

  const cachedPosIdea = client.readQuery<IdeaPositionsQuery>({
    query: IdeaPositions,
    variables: { ideaId }
  })?.idea;

  const { data: updateData, loading: updateLoading, error: updateError } = useIdeaUpdatesQuery({
    variables: { id: ideaId }
  });

  if (updateError) throw updateError;

  const loading = isLoading || updateLoading;
  const { positions } = cachedPosIdea || {};
  const { ideaUpdates } = updateData?.idea || {};
  const { user: ideaAuthor, isNonBuyside } = cachedIdeaAuthor || {};
  const {
    anonymous,
    canCurrentUserViewIdeaFilterDetails,
    canCurrentUserAccessIdeaComments,
    canCurrentUserViewIdeaAttachments,
    closedAt,
    ideaUpdatesCount
  } = cachedIdea || {};
  const { id: currentUserId, isAdmin, canViewUserDetails } = cachedCurrentUser || {};

  const isCurrencySubunit = positions ? Boolean(positions[0].isCurrencySubunit) : false;

  return loading || isAuthor || Boolean(isAdmin) || ideaUpdatesCount ? (
    <div id="idea_updates">
      <Accordion basic initialOpen={canCurrentUserViewIdeaFilterDetails && window.innerWidth > 768}>
        <AccordionTitle
          collapseIcon={faMinus}
          dataTag="updates"
          expandable={Boolean(canCurrentUserViewIdeaFilterDetails && ideaUpdates?.length)}
          expandIcon={faPlus}
          isLocked={
            canCurrentUserViewIdeaFilterDetails !== undefined &&
            !canCurrentUserViewIdeaFilterDetails
          }
          loading={loading}
        >
          {`Updates ${ideaUpdatesCount || ideaUpdatesCount === 0 ? `(${ideaUpdatesCount})` : ''}`}
        </AccordionTitle>
        <AccordionContent>
          {!loading && (isAuthor || isAdmin) && !closedAt && (
            <IdeaUpdateModal
              analyticsTag="update-position"
              closeIdea={false}
              faIcon={faPlus}
              ideaId={ideaId}
              main
              modalText="Add Update / Adjust Position"
            />
          )}
          <div data-testid="ideaUpdates">
            {loading ? (
              times(2).map(count => <IdeaUpdateCardLoading key={`update-loading-${count}`} />)
            ) : (
              <>
                {ideaAuthor?.id !== currentUserId &&
                (ideaUpdatesCount || 0) > 0 &&
                ideaUpdates?.length !== ideaUpdatesCount &&
                !isPublic ? (
                  <DelayedAccessAlert
                    id={Location.ideaUpdates}
                    sectionName="idea updates and any comments on them"
                  />
                ) : null}
                {ideaUpdates?.map((ideaUpdate, idx) =>
                  ideaUpdate ? (
                    <IdeaUpdateCard
                      authorId={ideaAuthor?.id}
                      authorName={authorDisplayName}
                      canAccessComments={canCurrentUserAccessIdeaComments}
                      canCurrentUserViewIdeaAttachments={canCurrentUserViewIdeaAttachments}
                      canWriteComments={canWriteComments}
                      canViewUserDetails={canViewUserDetails}
                      currentUserId={currentUserId}
                      ideaId={ideaId}
                      ideaUpdate={ideaUpdate}
                      isAdmin={isAdmin}
                      isAuthor={isAuthor}
                      isCurrencySubunit={isCurrencySubunit}
                      isIdeaAnonymous={anonymous}
                      isNonBuyside={Boolean(isNonBuyside)}
                      key={idx}
                    />
                  ) : null
                )}
              </>
            )}
          </div>
        </AccordionContent>
      </Accordion>
    </div>
  ) : null;
};

export default IdeaUpdates;
